import React, { useState, useEffect, useRef } from "react";
import swal from "sweetalert";
import { toast } from "react-toastify";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";

import { customStyles } from "../../../Bookclosure/Components/AddTaskModal";
import commoncss from "./../../css/common.module.css";
import buttoncss from "./../../css/button.module.css";
import libcss from "./../../css/library.module.css";
import Modal from "./../../elements/Modal";
import { Button } from "./../../elements/Button";
import Dropdown from "./../../elements/Dropdown";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/formulaPlus.svg";
import { ReactComponent as AddPlusIcon } from "../../assets/icons/grayPlus.svg";
import { ReactComponent as MinusIcon } from "../../assets/icons/MinusIcon.svg";
import { ReactComponent as MultiplyIcon } from "../../assets/icons/MultiplyIcon.svg";
import { ReactComponent as DivisionIcon } from "../../assets/icons/DivisionIcon.svg";
import InputGroup from "./../../elements/InputGroup";
import CustomDropdownIndicator from "../../elements/CustomDropdownIndicator";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import adminPortalCss from "../../css/adminportal.module.css";

const animatedComponents = makeAnimated();

const AddItemModal = ({
  groupingData,
  addItemLevel,
  addItemCat,
  addItemHandler,
  addToSubgroup,
  setAddItemModal,
  l1id,
  l1idx,
  l2idx,
  l3idx,
}) => {
  const [selectedItem, setSelectedItem] = useState("line item");
  const [itemName, setItemName] = useState(null);
  const [lineItemNature, setLineItemNature] = useState("credit");

  // ensure every item has operator
  const [formulaItems, setFormulaItems] = useState([
    { element: Date.now(), value: "", operator: "positive" },
  ]);

  const operatorOptions = [
    { value: "positive", label: "positive", symbol: "+", Icon: PlusIcon },
    { value: "negative", label: "negative", symbol: "-", Icon: MinusIcon },
    // { value: "multiply", label: "multiply", symbol: "×", Icon: MultiplyIcon },
    // { value: "divide", label: "divide", symbol: "÷", Icon: DivisionIcon },
  ];

  const ReturnItemsObj = () => {
    const returnFormulaItems = formulaItems
      .filter(
        (x) =>
          x.value &&
          (x.value.value || x.value.label || typeof x.value === "string")
      )
      .map((x) => ({
        name:
          typeof x.value === "string"
            ? x.value
            : x.value.value ?? x.value.label ?? "",
        sign: x.operator ?? "positive",
      }));

    const addItemData = {
      level: addItemLevel,
      category: addItemCat,
      lineType: selectedItem,
      name: itemName,
      nature: lineItemNature,
      formula: returnFormulaItems,
      subgroup: [],
    };
    if (addItemLevel === 2 || addItemLevel === 3) {
      delete addItemData.category;
    }
    if (addItemLevel === 3) {
      delete addItemData.subgroup;
    }

    if (addItemLevel === 1) {
      addItemHandler(addItemData);
    }
    const l1index = l1id;
    if (addItemLevel === 2) {
      addToSubgroup([l1index], addItemData, addItemCat);
    }
    if (addItemLevel === 3) {
      addToSubgroup([l1index, l2idx], addItemData, addItemCat);
    }
  };

  // 🔹 choose the correct ROOT branch, same idea as ShowEditItemModal
  const getRootItemsForFormula = () => {
    if (!Array.isArray(groupingData)) return [];

    // Level 1 → whole top-level list
    if (addItemLevel === 1) {
      return groupingData;
    }

    // Level 2 → subgroup under selected L1 index
    if (addItemLevel === 2) {
      if (typeof l1idx === "number" && groupingData[l1idx]) {
        const l1 = groupingData[l1idx];
        return Array.isArray(l1?.subgroup) ? l1.subgroup : [];
      }
      return groupingData;
    }

    // Level 3 → subgroup under selected L2 inside selected L1
    if (addItemLevel === 3) {
      if (
        typeof l1idx === "number" &&
        typeof l2idx === "number" &&
        groupingData[l1idx]
      ) {
        const l1 = groupingData[l1idx];
        const l2 = Array.isArray(l1?.subgroup) ? l1.subgroup[l2idx] : null;
        return Array.isArray(l2?.subgroup) ? l2.subgroup : [];
      }
      return groupingData;
    }

    return groupingData;
  };

  // 🔹 ONLY DROPDOWN OPTIONS LOGIC CHANGED: ignore category, filter by level & lineType
  const extractItems = (items = [], levelToMatch) => {
    const result = [];
    const seen = new Set();

    const traverse = (nodes) => {
      if (!Array.isArray(nodes)) return;
      for (const node of nodes) {
        // same level & only line items as formula operands
        if (node.level === levelToMatch && node.lineType === "line item") {
          const key = `${node.level}||${node.name}`;
          if (!seen.has(key)) {
            seen.add(key);
            result.push({ label: node.name, value: node.name });
          }
        }

        if (Array.isArray(node.subgroup) && node.subgroup.length) {
          traverse(node.subgroup);
        }
      }
    };

    traverse(items);
    return result;
  };

  // 🔹 use branch root + level, no category filter (matches working edit logic)
  const formulaItemList = extractItems(getRootItemsForFormula(), addItemLevel);

  // add new element (default operator included)
  const addFormulaItem = () => {
    setFormulaItems((prev) => [
      ...prev,
      {
        element: Date.now() + Math.floor(Math.random() * 1000),
        value: "",
        operator: "positive",
      },
    ]);
  };

  const updateFormulaItem = (index, value) => {
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], value };
      return copy;
    });
  };

  const updateFormulaOperator = (index, operator) => {
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], operator };
      return copy;
    });
  };

  // operator dropdown control (closes on outside click / Esc)
  const OperatorDropdown = ({
    value,
    onSelect,
    className = "",
    style = {},
  }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected =
      operatorOptions.find((o) => o.value === value) ?? operatorOptions[0];

    useEffect(() => {
      const handleDocClick = (e) => {
        if (!ref.current) return;
        if (!ref.current.contains(e.target)) setOpen(false);
      };
      const handleEsc = (e) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", handleDocClick);
      document.addEventListener("keydown", handleEsc);
      return () => {
        document.removeEventListener("mousedown", handleDocClick);
        document.removeEventListener("keydown", handleEsc);
      };
    }, []);

    return (
      <div
        ref={ref}
        className={className}
        style={{ position: "relative", display: "inline-block", ...style }}
      >
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          aria-haspopup="menu"
          aria-expanded={open}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            // padding: "6px 8px",
            // borderRadius: 6,
            border: "1px solid #fff",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {/* render icon component directly so it doesn't get lost */}
            {selected && selected.Icon ? <selected.Icon /> : <PlusIcon />}
          </span>
        </button>

        {open && (
          <div
            role="menu"
            aria-label="Choose operator"
            style={{
              position: "absolute",
              // minWidth: 100,
              top: "calc(100% + 6px)",
              left: 0,
              background: "#fff",
              border: "1px solid #ddd",
              boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
              borderRadius: 6,
              padding: 6,
              zIndex: 50,
            }}
          >
            {operatorOptions.map((op) => {
              const IconComp = op.Icon;
              return (
                <div
                  key={op.value}
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => {
                    onSelect(op.value);
                    setOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(op.value);
                      setOpen(false);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    cursor: "pointer",
                    borderRadius: 4,
                    width: "fit-content",
                  }}
                >
                  <span
                    style={{ display: "inline-flex", alignItems: "center" }}
                  >
                    <IconComp />
                  </span>
                  {/* <span
                    style={{
                      opacity: 0.85,
                      fontSize: 13,
                      textTransform: "none",
                    }}
                  >
                    {op.label}
                  </span> */}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // build formula display text using operator symbols (safe)
  const formulaDisplayString = (() => {
    if (!formulaItems || formulaItems.length === 0) return "";
    const pieces = formulaItems.map((it) => {
      if (!it || !it.value) return "(?)";
      if (typeof it.value === "string") return it.value;
      if (it.value.label) return it.value.label;
      return it.value.value ?? "(?)";
    });
    let display = "";
    for (let i = 0; i < pieces.length; i++) {
      display += pieces[i];
      if (i < pieces.length - 1) {
        const op = operatorOptions.find(
          (o) => o.value === formulaItems[i].operator
        );
        display += ` ${op?.symbol ?? "+"} `;
      }
    }
    return display;
  })();

  return (
    <Modal
      width={"35%"}
      minHeight={"200px"}
      title={`Add Level ${addItemLevel} Item`}
      bodycss={commoncss.unsetPadding}
      headerActions={
        <>
          <CloseIcon
            className={commoncss.clickable}
            onClick={() => {
              setAddItemModal(false);
            }}
          />
        </>
      }
      onClose={() => console.log("Modal Closed")}
      footerActions={
        <>
          <Button
            btnColor={buttoncss.secondary}
            onClick={() => setAddItemModal(false)}
            label="Cancel"
          />
          <span>
            &ensp;
            <Button
              btnColor={buttoncss.primary}
              label="Create"
              onClick={ReturnItemsObj}
            />
          </span>
        </>
      }
    >
       <div className={commoncss.pd1530}>
        <div className={adminPortalCss.radioButtonSection}>
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="responsibilities"
            value={selectedItem}
            onChange={(e) => setSelectedItem(e.target.value)}
            className={adminPortalCss.radioSection}
          >
            <FormControlLabel
              value="line item"
              className={adminPortalCss.radioLabel}
              control={
                <Radio
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "#D6D6D6",
                    "&.Mui-checked": { color: "#3AB67D" },
                  }}
                />
              }
              label="Line Item"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight: 500,
                  textTransform: "none",
                  color: "#111111",
                  fontSize: "14px",
                },
              }}
            />
            <FormControlLabel
              className={adminPortalCss.radioLabel}
              value="formula"
              control={
                <Radio
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "#D6D6D6",
                    "&.Mui-checked": { color: "#3AB67D" },
                  }}
                />
              }
              label="Formula"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight: 500,
                  textTransform: "none",
                  color: "#111111",
                  fontSize: "14px",
                },
              }}
            />
          </RadioGroup>
        </div>

        {selectedItem === "line item" && (
          <>
            <InputGroup
              class_="mb-2"
              label="Line Item Name"
              inputs={[
                <>
                  <input
                    name="line item name"
                    className={adminPortalCss.lineItemNameInput}
                    value={itemName ?? ""}
                    onChange={(e) => setItemName(e.target.value)}
                    placeholder="Enter line item name"
                  />
                </>,
              ]}
            />
            {addItemLevel === 1 && (
              <div className={commoncss.dFlexJustifysb}>
                <span className={`${commoncss.label3} ${commoncss.fs13}`}>
                  Nature of Line Item
                </span>
                <div className={adminPortalCss.radioButtonSection}>
                  <RadioGroup
                    row
                    name="lineItemNature"
                    value={lineItemNature}
                    onChange={(e) => setLineItemNature(e.target.value)}
                    className={adminPortalCss.radioSection}
                  >
                    <FormControlLabel
                      value="credit"
                      className={adminPortalCss.radioLabel}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: "#D6D6D6",
                            "&.Mui-checked": { color: "#3AB67D" },
                          }}
                        />
                      }
                      label="Credit"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 500,
                          textTransform: "none",
                          color: "#111111",
                          fontSize: "13px",
                        },
                      }}
                    />
                    <FormControlLabel
                      value="debit"
                      className={adminPortalCss.radioLabel}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: "#D6D6D6",
                            "&.Mui-checked": { color: "#3AB67D" },
                          }}
                        />
                      }
                      label="Debit"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 500,
                          textTransform: "none",
                          color: "#111111",
                          fontSize: "13px",
                        },
                      }}
                    />
                  </RadioGroup>
                </div>
              </div>
            )}
          </>
        )}

        {selectedItem === "formula" && (
          <div>
            <InputGroup
              class_="mb-2"
              label="Formula Name"
              inputs={[
                <>
                  <input
                    name="Formula name"
                    value={itemName ?? ""}
                    onChange={(e) => setItemName(e.target.value)}
                    placeholder="Enter formula name"
                  />
                </>,
              ]}
            />

            <span className={`input-label ml-1`}>Build Formula</span>

            <div
              className={`${commoncss.dFlex} ${commoncss.gap20} ${
                libcss.formulaWrap || ""
              }`}
            >
              {formulaItems.map((item, index) => (
                <React.Fragment key={item.element ?? index}>
                  <div
                    className={commoncss.dFlex}
                    style={{ alignItems: "center", gap: "10px" }}
                  >
                    <div className={libcss.formulaCard}>
                      <span
                        className={`${commoncss.label3} ${commoncss.fw600}`}
                      >
                        LEVEL {addItemLevel}
                      </span>

                      <Dropdown
                        class2_={commoncss.bgWhite}
                        options={formulaItemList}
                        name="formula"
                        value={item.value}
                        onSelect={(value) => updateFormulaItem(index, value)}
                        placeHolder="Select item"
                      />
                    </div>

                    {/* INLINE OPERATOR DROPDOWN HERE */}
                    <OperatorDropdown
                      value={item.operator}
                      onSelect={(value) => updateFormulaOperator(index, value)}
                    />
                  </div>
                </React.Fragment>
              ))}

              <div
                className={`${libcss.addFormulaCard} ${commoncss.clickable}`}
                onClick={addFormulaItem}
              >
                <span className={`${commoncss.label3} ${commoncss.fw600}`}>
                  <AddPlusIcon />
                </span>
                <span className={`${commoncss.greyLabel}`}>Add Element</span>
              </div>
            </div>

            <div className={`${libcss.displayFormula} mt-3`}>
              <span className={`${commoncss.greyLabel} ${commoncss.fw500}`}>
                Formula
              </span>
              <span className={`${commoncss.label5} ${commoncss.fw500}`}>
                Formula Name = {formulaDisplayString || "(no formula selected)"}
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddItemModal;
